import { route } from 'preact-router';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { DrivingAreaCard, type PickupZone } from '../../components/booking/DrivingAreaCard.js';
import { PaymentPauseNotice } from '../../components/booking/PaymentPauseNotice.js';
import { type AlternativeCar, AlternativeCars } from '../../components/ui/AlternativeCars.js';
import { Button } from '../../components/ui/Button.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { ResultState } from '../../components/ui/ResultState.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { TripContextStrip } from '../../components/ui/datetime/TripContextStrip.js';
import {
  isDateWithinWindow,
  rangeOverlapsAny,
} from '../../components/ui/datetime/useDateTimePicker.js';
import { goneMessage } from '../../components/ui/resultWording.js';
import { useAuth } from '../../context/AuthContext.js';
import { useFetch } from '../../hooks/useFetch.js';
import { useOccupiedWindows } from '../../hooks/useOccupiedWindows.js';
import { usePaymentPause } from '../../hooks/usePaymentPause.js';
import { useSignInRedirect } from '../../hooks/useSignInRedirect.js';
import { ApiError, api } from '../../services/api.js';
import type { LegalDocument } from '../../services/legalDocuments.js';
import { LEGAL_DOCUMENT_PATHS } from '../../services/legalDocuments.js';
import { type QuoteLine, promoSavings } from '../../utils/promos.js';
import { parseCarriedDate } from '../../utils/tripQuery.js';
import { PENALTY_UNPAID_SENTENCE, type PenaltyList, owesPenalty } from '../account/penalties.js';
import { CreditStep, type CreditSummary } from './CreditStep.js';
import { GiftCertStep } from './GiftCertStep.js';
import { type MileageOffer, MileagePackageStep } from './MileagePackageStep.js';
import { TripStep } from './TripStep.js';
import { VoucherStep } from './VoucherStep.js';
import { type QuoteMinimum, refusalSentence } from './quoteWording.js';
import {
  EMPTY_TRIP_DRAFT,
  type TripDraft,
  tripBlockedSentence,
  tripFromDraft,
} from './tripDraft.js';

// Server-resolved operating window (feature 039) — mirrors the backend's
// tagged 3-state EffectiveWindow; the app never re-derives it (FR-013).
type EffectiveOperatingWindow =
  | { status: 'open'; window: { start: string; end: string } }
  | { status: 'unrestricted' }
  | { status: 'blocked' };

interface CarSummary {
  /** 052: display label — brand + model. `name` is a `·`-joined spec dump. */
  label?: string;
  _id: string;
  name: string;
  // 126: city_name/address_line_1 were already on the wire (CarDetailPage.tsx already
  // reads them) — this type just under-declared them until now.
  // 401 (#401): `name` — the operator-given "Location Name" — is the primary pickup label;
  // see CarDetailPage.tsx's own comment on the same field for why.
  location?: { location_id?: string; name?: string; city_name?: string; address_line_1?: string };
  pricing_template_ids?: string[];
  effective_operating_window?: EffectiveOperatingWindow;
  /** 416 (#1431): the car's pickup area, shown before paying; null when the location has no zone. */
  pickup_zone?: PickupZone | null;
}

interface BookingFormProps {
  id?: string;
  path?: string;
  // 126: whatever window the customer already chose while searching, carried onto
  // this page instead of dropped — location_id is deliberately not part of this
  // (research.md D1).
  pickup_at?: string;
  return_at?: string;
  /**
   * 304: present only when reached via BookingConfirmPage's "Extend" button, whose own
   * `GET /bookings/:id/extend-context` call already resolved and validated `id` (the car) and
   * `pickup_at` (locked to the parent's current return_at) before navigating here — this page
   * trusts neither blindly, `POST /bookings` re-validates both server-side, but locks the pickup
   * control so the customer cannot pick a value the server would only reject anyway.
   */
  parent_booking_id?: string;
}

interface QuoteResponse {
  outcome: string;
  base_minor?: number;
  total_minor?: number;
  personal_discount?: { applied?: boolean; discount_type?: string; discount_minor?: number };
  // 065: `reason` was absent here while the platform had been sending it all along
  // (`pricing/routes.ts:173`, `:195`, `:275`), so the field was discarded at this boundary and an
  // invalid voucher code produced no message anywhere in the app. Gap-list F3.
  voucher?: { applied?: boolean; reason?: string; discount_minor?: number };
  // 097: present only when outcome === 'priced'.
  platform_fee?: { enabled: boolean; amount_minor?: number };
  channel_fee?: { enabled: boolean; amount_minor?: number };
  tax?: { rate_bps: number; taxable_base_minor: number; tax_minor: number };
  grand_total_minor?: number;
  gift_certificate?: {
    applied: boolean;
    reason?: string;
    code?: string;
    credit_minor?: number;
    balance_after_minor?: number;
  };
  // 358: the "Use my credit" preview (contracts/credit.md).
  credit?:
    | { applied: true; credit_minor: number; remaining_minor: number }
    | { applied: false; reason?: string };
  amount_due_minor?: number;
  // 091: the rate card the quote actually priced. The car's `pricing_template_ids[0]` is the first
  // LISTED card; this is the first APPLICABLE one (`pricing/select-template.ts:17-24`), and for a
  // car carrying more than one they differ exactly when the first does not cover the window.
  pricing_template_id?: string;
  // 091: present only on `min_not_met`. Absent means "not stated", never "no minimum".
  minimum?: QuoteMinimum;
  // 400 (#1354): how each rule changed the price; a time-of-day promo's lines name what it saved.
  breakdown?: QuoteLine[];
  // 417 (#1464): the packages offered (null when none is) and the one priced.
  mileage_offer?: MileageOffer | null;
  mileage_package?: { package: 1 | 2; price_minor: number; allowance_km: number | null };
}

function rm(minor: number): string {
  return `RM ${(minor / 100).toFixed(2)}`;
}

/** 358: credit priced into this quote, from "Use my credit" or from a credit code (never both: one tender). */
function creditUsedMinor(quote: QuoteResponse): number {
  if (quote.credit?.applied) return quote.credit.credit_minor;
  if (quote.gift_certificate?.applied) return quote.gift_certificate.credit_minor ?? 0;
  return 0;
}

export function BookingFormPage({ id, pickup_at, return_at, parent_booking_id }: BookingFormProps) {
  const isExtending = Boolean(parent_booking_id);
  const goToSignIn = useSignInRedirect();
  const carRead = useFetch<CarSummary>(id ? `/cars/${id}` : null);
  const { data: car, status: carStatus } = carRead;
  // `idle` means no id in the address yet — nothing has been asked, so nothing can be reported.
  const loading = carStatus === 'loading' || carStatus === 'idle';
  // Pickup/return are Date objects driven by the DateRangePicker; the submit
  // payload still serialises them to ISO strings (unchanged wire contract, FR-008).
  const [pickupAt, setPickupAt] = useState<Date | null>(null);
  const [returnAt, setReturnAt] = useState<Date | null>(null);
  // Stable "now" floor so the picker doesn't churn its minDate every render.
  const now = useMemo(() => new Date(), []);
  const [voucherCode, setVoucherCode] = useState('');
  const [giftCertCode, setGiftCertCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // 385 (#1304): the guard against a second submit; state is a render behind, so it cannot be the guard itself.
  const submittingRef = useRef(false);
  // 153 (#23): the customer's agreement to the rental terms. Never pre-ticked (FR-002) — consent
  // is something they give, not something the form assumes on their behalf.
  const [termsAgreed, setTermsAgreed] = useState(false);
  // 361 (#980): the trip answers. Asked on an ordinary booking only: an extension copies the original's.
  const [tripDraft, setTripDraft] = useState<TripDraft>(EMPTY_TRIP_DRAFT);
  const trip = isExtending ? null : tripFromDraft(tripDraft);
  const tripComplete = isExtending || trip !== null;
  // 417 (#1464): Package 1 is chosen to start with. Never asked on an extension: it keeps the rental's package.
  const [mileageChoice, setMileageChoice] = useState<1 | 2>(1);
  /**
   * 160 (FR-011): the revision of the terms this renter is being shown.
   *
   * 🔴 Read ONCE when the form loads and sent unchanged at submit. If an operator publishes new
   * terms while somebody is half-way through a booking, the agreement recorded must be the text
   * that person actually saw — re-reading it at submit would record consent to wording they were
   * never shown.
   */
  const termsRead = useFetch<LegalDocument>('/legal-documents/terms_of_service');
  const termsRevision = termsRead.status === 'ok' ? termsRead.data : null;
  const [error, setError] = useState('');
  // 330 (FR-013): the platform refused because the car is not at the customer's campus.
  const [campusMismatch, setCampusMismatch] = useState(false);
  const { user, refreshUser } = useAuth();
  // 358 (#1091): the customer's credit, offered at checkout. A guest has none to read.
  const creditRead = useFetch<CreditSummary>(user ? '/credit' : null);
  // 393 (#1313, FR-006): an unpaid penalty stops a new booking (never an extension). Said before the form is filled in;
  // the platform refuses it too (403 PENALTY_UNPAID).
  const penaltiesRead = useFetch<PenaltyList>(user && !isExtending ? '/penalties/mine' : null);
  const owesPenaltyNow = !isExtending && owesPenalty(penaltiesRead.data);
  const credit = creditRead.status === 'ok' ? creditRead.data : null;
  const [useCredit, setUseCredit] = useState(false);
  // 358 (FR-010): bumped when the platform refused because the credit changed, to price the booking again.
  const [requoteKey, setRequoteKey] = useState(0);
  // 213 (#309): populated only when the refusal that set `error` carried alternatives.
  const [alternatives, setAlternatives] = useState<AlternativeCar[]>([]);

  /**
   * 091: the platform's answer together with the WINDOW that produced it.
   *
   * The pair exists for the same reason `voucherAnswer` below does — an answer decides nothing once
   * the question has changed — but it is keyed on the window **alone**, and that is the whole
   * design. A voucher or gift-certificate keystroke leaves the key untouched, so the retained price
   * still renders and the blanking 075 removed does not come back. A date change does move the key,
   * and a price for another window is exactly what must not be believed: since 091 the submitted
   * `pricing_template_id` is read out of this answer, so a stale pair would book a rate card the
   * renter was never quoted (US3), not merely show a stale number.
   */
  const [quoteAnswer, setQuoteAnswer] = useState<{ key: string; response: QuoteResponse } | null>(
    null,
  );
  const [quoting, setQuoting] = useState(false);
  const [quoteError, setQuoteError] = useState('');
  /**
   * 075: the platform's verdict together with the voucher code that produced it.
   *
   * Kept separately from `quote` because the two have different lifetimes — `quote` is deliberately
   * retained across a refetch so the price summary does not blank on every keystroke, whereas this
   * pair exists to be compared against what is in the box right now. `null` means nothing has been
   * asked yet; a `verdict` of `undefined` means the platform answered without mentioning a voucher,
   * which is what happens when no code was sent. Absent is not a refusal.
   */
  const [voucherAnswer, setVoucherAnswer] = useState<{
    code: string;
    verdict?: QuoteResponse['voucher'];
  } | null>(null);

  // Operating hours (039): open → constrain picker slots; blocked → whole
  // funnel unavailable; unrestricted/absent → no constraint.
  const operatingWindow = car?.effective_operating_window;
  const isBlocked = operatingWindow?.status === 'blocked';
  const allowedWindow = operatingWindow?.status === 'open' ? operatingWindow.window : null;

  /**
   * 205 (#292): already-committed windows for this car — a paid/confirmed booking's hold, or a
   * maintenance/staff block — read once per car id (research.md R5), never re-fetched as the
   * customer changes their selection. Public endpoint, no auth needed.
   */
  // 386 (#1301): the same read the car page now makes (`useOccupiedWindows`).
  // 386 slice 2: an extension leaves its own rental (and its gap) out of the windows.
  const occupied = useOccupiedWindows(
    id,
    parent_booking_id ? { rental: parent_booking_id } : undefined,
  );
  const occupiedWindows = occupied.windows;

  /**
   * 126: a carried-forward window pre-fills exactly as if the customer had picked it
   * by hand — but only when BOTH dates parse, respect the same min-duration rule the
   * picker itself enforces, and pass the same `minDate`/`allowedWindow` checks a
   * manually-chosen window is already held to (`isDateWithinWindow`, shared with the
   * picker's own validation — research.md D4). Anything less falls back to exactly
   * today's empty, unselected form — never a partial fill (spec FR-005).
   *
   * `attemptedPrefill` guards against ever doing this twice — `car` is a fresh object
   * on every `useFetch` resolution, so a plain `[carStatus]` dependency array would be
   * inexhaustive, but depending on `car` itself without the guard would silently
   * re-run this and clobber a date the customer had since picked by hand.
   */
  const attemptedPrefill = useRef(false);
  useEffect(() => {
    if (attemptedPrefill.current || carStatus !== 'ok' || !car) return;
    // 386 (#1301, FR-016): an ordinary carried window is checked against the car's other rentals, so wait until that
    // read is over (an extension's pickup is not checked, below, and does not wait).
    if (!isExtending && !occupied.settled) return;
    attemptedPrefill.current = true;
    const carriedPickup = parseCarriedDate(pickup_at);

    // 304: an extension's pickup is not a carried search preference to sanity-check against the
    // car's window — it is the authoritative continuation point extend-context already resolved
    // and validated server-side. Set it unconditionally and leave return_at for the customer to
    // choose; the normal carried-window checks below are for the ordinary search-to-booking path.
    if (isExtending) {
      if (carriedPickup) setPickupAt(carriedPickup);
      return;
    }

    const carriedReturn = parseCarriedDate(return_at);
    if (!carriedPickup || !carriedReturn) return;
    if (carriedReturn.getTime() - carriedPickup.getTime() < 30 * 60 * 1000) return;
    if (car.effective_operating_window?.status === 'blocked') return;
    const window =
      car.effective_operating_window?.status === 'open'
        ? car.effective_operating_window.window
        : null;
    if (!isDateWithinWindow(carriedPickup, { minDate: now, allowedWindow: window })) return;
    if (!isDateWithinWindow(carriedReturn, { minDate: now, allowedWindow: window })) return;
    // 386 (#1301, FR-016): a window that crosses another rental would only be refused later (at Book or payment); start
    // empty instead, as for any other invalid carried window. A failed read has no windows, so it keeps the old
    // behaviour by itself.
    if (rangeOverlapsAny(occupied.windows, carriedPickup, carriedReturn)) return;
    setPickupAt(carriedPickup);
    setReturnAt(carriedReturn);
  }, [carStatus, car, pickup_at, return_at, now, isExtending, occupied.settled, occupied.windows]);

  // A voucher and a gift certificate are mutually exclusive per booking (contract 400).
  // Reflect it in the UI before the server rejects: never send both.
  //
  // 075: the exclusivity binds on an **accepted** voucher, not on a non-empty box. Deriving it from
  // the input meant a code the platform had refused still withdrew the gift-certificate tender,
  // while the refusal was stated one control above — the screen contradicting itself and denying a
  // tender the renter was entitled to (FR-001, FR-002).
  //
  // The platform's answer does not say which code it judged (`pricing/quote.ts:28` carries only
  // `{applied, reason?, discount_minor?}`), so the verdict is held beside the code that produced it
  // and is believed only while that code is still the one in the box (FR-006). A pair that has been
  // superseded by an edit answers a question no longer being asked, and decides nothing.
  const typedVoucherCode = voucherCode.trim();
  const currentVerdict =
    voucherAnswer && voucherAnswer.code === typedVoucherCode ? voucherAnswer.verdict : undefined;
  const isVoucherApplied = currentVerdict?.applied === true;
  // A code is in the box and nothing answers it yet — in flight, or superseded by an edit. The
  // tender is held rather than opened or closed, so a gift-certificate code cannot come into
  // existence while the voucher's fate is undecided (FR-007).
  const isVerdictPending = typedVoucherCode.length > 0 && currentVerdict === undefined;

  /**
   * What each request carries, per state — see `specs/075-refused-voucher-tender/contracts/`.
   *
   * The platform answers a body carrying **both** a voucher code and a gift-certificate code with a
   * 400, and its guard tests the presence of the strings rather than the verdict
   * (`pricing/routes.ts:129`, `bookings/service.ts:97`). So a refused code is as fatal as an
   * accepted one once the renter reaches for their gift certificate.
   *
   * The omission is scoped to exactly that case. With no gift certificate in play the refused code
   * is still sent, unchanged — 065's FR-012 keeps the platform as the enforcement authority, and a
   * code the renter typed is not discarded on the strength of an answer that may already be stale.
   */
  const isGiftCertApplied =
    !isVoucherApplied && !isVerdictPending && giftCertCode.trim().length > 0;
  // 358 (FR-014): credit is a tender like a code, held to the same rules: not while a voucher is accepted or its
  // verdict is pending, and not beside a typed code.
  const isCreditApplied =
    useCredit && !isVoucherApplied && !isVerdictPending && giftCertCode.trim().length === 0;
  const sendVoucherCode = typedVoucherCode.length > 0 && !isGiftCertApplied && !isCreditApplied;
  const datesValid =
    pickupAt !== null &&
    returnAt !== null &&
    returnAt.getTime() - pickupAt.getTime() >= 30 * 60 * 1000;

  // The identity of the window currently selected. `null` while there is nothing to ask about.
  const windowKey =
    pickupAt && returnAt ? `${pickupAt.toISOString()}|${returnAt.toISOString()}` : null;
  // The answer, but only while it is about the window on screen. Everything downstream — the price
  // summary, the refusal, whether Continue may be pressed, and which rate card is submitted — reads
  // this and never `quoteAnswer` directly.
  const quote =
    quoteAnswer && windowKey && quoteAnswer.key === windowKey ? quoteAnswer.response : null;

  // Fetch a live quote whenever dates or voucher change.
  // 358: `requoteKey` is never read in the body; bumping it is what prices the booking again after CREDIT_CHANGED,
  // the same shape as useFetch's `attempt`.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see the note above
  useEffect(() => {
    if (!id || !datesValid || !pickupAt || !returnAt) {
      setQuoteAnswer(null);
      return;
    }
    let cancelled = false;
    // The window this request is about, captured before it goes out — not read back from state when
    // it lands, which is precisely when the two can differ.
    const askedWindowKey = `${pickupAt.toISOString()}|${returnAt.toISOString()}`;
    // Captured before the request goes out: this is the code the answer will be about, and whether
    // this request is the kind that can produce an answer about it at all.
    const askedVoucherCode = voucherCode.trim();
    const askedSentVoucherCode = sendVoucherCode;
    setQuoting(true);
    setQuoteError('');
    api
      .post<QuoteResponse>('/pricing/quote', {
        car_id: id,
        pickup_at: pickupAt.toISOString(),
        return_at: returnAt.toISOString(),
        voucher_code: sendVoucherCode ? voucherCode : undefined,
        gift_cert_code: isGiftCertApplied ? giftCertCode.trim() : undefined,
        use_credit: isCreditApplied ? true : undefined,
        // 417: the platform prices the choice where it offers packages and ignores it elsewhere.
        mileage_package: isExtending ? undefined : mileageChoice,
      })
      .then((res) => {
        if (!cancelled) {
          setQuoteAnswer({ key: askedWindowKey, response: res });
          // Pair the answer with the code this request actually carried, not with whatever is in
          // the box by the time it lands — those differ precisely when the renter kept typing.
          //
          // Only a request that CARRIED the code produces an answer about it. A request that
          // deliberately omitted it comes back with no `voucher` key at all
          // (`pricing/routes.ts:288` assigns `voucher` only when a code arrived), so overwriting
          // here would erase the refusal at the exact moment the renter acted on it (FR-015) — and
          // worse, it would oscillate: verdict cleared → tender no longer "refused" → the code is
          // sent again → refused again → cleared again, one request per cycle, forever.
          if (askedSentVoucherCode) {
            setVoucherAnswer({ code: askedVoucherCode, verdict: res.voucher });
          }
        }
      })
      .catch((err: { message?: string }) => {
        if (!cancelled) {
          setQuoteAnswer(null);
          setQuoteError(err.message ?? 'Failed to price this booking');
        }
      })
      .finally(() => {
        if (!cancelled) setQuoting(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    id,
    pickupAt,
    returnAt,
    voucherCode,
    giftCertCode,
    isGiftCertApplied,
    isCreditApplied,
    requoteKey,
    sendVoucherCode,
    datesValid,
    mileageChoice,
  ]);

  async function handleSubmit(e: Event) {
    e.preventDefault();
    // 385 (#1304): a second submit while the first is in flight sends nothing; each press used to create a booking.
    if (submittingRef.current) return;
    // 091 US1: the invariant, not just the rendering. A disabled button is a fact about one frame;
    // this is the guarantee that nothing is sent for a window the platform has already refused.
    if (!id || !pickupAt || !returnAt || isBlocked || !canSubmit) return;
    // 091 US3: the card the quote priced — never the car's first listed one. Those differ exactly
    // when the first card does not apply to this window, which is the case that showed the renter a
    // valid price and then refused them at submission.
    const quotedTemplateId = quote?.pricing_template_id;
    if (!quotedTemplateId) {
      // Unreachable against the platform as it stands — a priced quote always names its card
      // (`pricing/quote.ts`'s priced return). Stated rather than silently dropped anyway: a press
      // that does nothing and says nothing is the failure mode this whole feature exists to end.
      setError('This booking could not be priced. Please choose your dates again.');
      return;
    }
    const locationId = car?.location?.location_id;
    if (!locationId) {
      setError('This car has no pickup location configured.');
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    setError('');
    setAlternatives([]);
    try {
      const booking = await api.post<{ id: string }>('/bookings', {
        car_id: id,
        location_id: locationId,
        pricing_template_id: quotedTemplateId,
        pickup_at: pickupAt.toISOString(),
        return_at: returnAt.toISOString(),
        // Same rule as the pricing request, and it has to be repeated here because this is a second
        // body: `POST /bookings` runs the identical both-codes guard (`bookings/service.ts:97`), so
        // a refused code left in the box would 400 the booking at the last step of the funnel.
        voucher_code: sendVoucherCode ? voucherCode : undefined,
        gift_cert_code: isGiftCertApplied ? giftCertCode.trim() : undefined,
        use_credit: isCreditApplied ? true : undefined,
        // 153 (#23): which text the renter was shown. The platform stamps the MOMENT itself — a
        // client-supplied timestamp is a claim about consent made by the party with an interest in
        // it, and adjustable from any browser console.
        //
        // 160: the revision id travels beside the version, and both come from the revision this
        // form LOADED, never from a fresh read at submit.
        terms_version: termsRevision?.version,
        terms_revision_id: termsRevision?.id,
        parent_booking_id,
        // 361: the platform requires these on an ordinary booking and ignores them on an extension.
        ...(trip ? { trip } : {}),
        // 417: required where the quote offered packages, refused elsewhere.
        ...(mileageOffer ? { mileage_package: mileageChoice } : {}),
      });
      route(`/booking/${booking.id}/confirm`);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : '';
      // 330 (FR-013): a campus refusal, said in the customer's words rather than the platform's.
      // 358 (FR-010): the credit moved between the price shown and the booking (another booking spent it, staff
      // revoked a certificate). Nothing was spent; price again so the customer sees the new figures before paying.
      if (code === 'CREDIT_CHANGED') {
        setError('Your credit changed while you were booking. Please check the new amount.');
        setRequoteKey((k) => k + 1);
        submittingRef.current = false;
        setSubmitting(false);
        return;
      }
      if (code === 'CAMPUS_REQUIRED') {
        // No campus: re-read the account, and the guard brings back the campus choice.
        await refreshUser();
        submittingRef.current = false;
        setSubmitting(false);
        return;
      }
      if (code === 'CAMPUS_MISMATCH') {
        // A stale page: staff may have moved the customer's campus since it loaded. Re-read the account
        // so the sentence and its link name the campus the customer has NOW (Gate A found the old one).
        await refreshUser();
      }
      setCampusMismatch(code === 'CAMPUS_MISMATCH');
      setError(
        code === 'CAMPUS_MISMATCH'
          ? ''
          : ((err as { message?: string }).message ?? 'Failed to create booking'),
      );
      setAlternatives(err instanceof ApiError ? (err.alternatives as AlternativeCar[]) : []);
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  if (loading)
    return (
      <div className="flex justify-center py-16">
        <Spinner size="lg" />
      </div>
    );

  // 064: a form for a car the platform will not serve is the app's most misleading screen — the
  // renter fills it in and only learns on submit that there was never anything to book (SC-004).
  if (carStatus !== 'ok')
    return (
      <ResultState
        state={carRead}
        onRetry={carRead.retry}
        goneMessage={goneMessage('car')}
        onwardLabel="Back to search"
        onOnward={() => route('/search')}
        onSignIn={goToSignIn}
      />
    );

  const priced = quote?.outcome === 'priced';
  // 417: the packages, only on an ordinary booking whose quote offers them.
  const mileageOffer = !isExtending && priced ? (quote?.mileage_offer ?? null) : null;
  /**
   * 091 US1: what may be submitted.
   *
   * This used to be `disabled={quoting || isBlocked}` while the price summary was gated on
   * `priced` — so a refusal rendered an empty box under a live button, and the renter first heard
   * of it from `POST /bookings`, in the engine's own words. The platform had already said so, at
   * 200, one request earlier.
   *
   * `priced` is read from the window-paired `quote`, so a price for a window nobody is asking about
   * cannot authorise a submission. Note what is deliberately NOT here: any test of the amount. Zero
   * is a price — it is what a gift certificate covering the total looks like — and gating on a
   * non-zero total would refuse exactly those bookings.
   */
  // 153 (#23): the agreement is one more condition, composed with the rest rather than replacing
  // any of them — whatever already blocked submission still blocks it, with its own wording.
  const bookable = Boolean(datesValid && !isBlocked && !quoting && !quoteError && priced && quote);
  // 406 (#1369): during the banks' Friday downtime the platform refuses a customer's booking or extension, which
  // could only wait unpaid; the form says so in place of Continue.
  const { paused: paymentsPaused } = usePaymentPause();
  const canSubmit = bookable && tripComplete && termsAgreed && !owesPenaltyNow && !paymentsPaused;
  // The platform answered, and its answer was a refusal. Distinct from `quoteError`, which means it
  // did not answer at all: a failed read is not a statement about this car.
  const refusalOutcome = quote && !priced ? quote.outcome : null;
  /**
   * Why Continue cannot be pressed, when the reason is not already on screen. An unavailable
   * control with no stated reason is not an acceptable outcome of this feature (FR-003) — the
   * blocked-car sentence, the quote error and the refusal each state their own, so this covers only
   * what is left: nothing has been asked yet.
   */
  const blockedReason =
    !canSubmit && !isBlocked && !quoting && !quoteError && !refusalOutcome && !datesValid
      ? 'Choose a pickup and return time to see the price for this car.'
      : // 153 (#23): everything else is satisfied and the agreement is the one thing missing. Last
        // in the chain deliberately — a renter who has not chosen dates yet is told about the dates,
        // not about terms they have no reason to read yet (FR-011).
        // 361 (design 2b): the trip answers come before the agreement, in the order they sit on the page.
        bookable && !tripComplete
        ? tripBlockedSentence(tripDraft)
        : bookable && !termsAgreed
          ? 'Please agree to the rental terms to continue.'
          : null;

  return (
    <div className="bg-bg px-4 py-6">
      {/* 208 (#308): had no back control at all before this feature — falls back to the car
          being booked, since that's where this form is always reached from. */}
      <PageHeader
        title={isExtending ? 'Extend Booking' : `Book ${car?.label ?? car?.name ?? 'Car'}`}
        fallback={`/cars/${id}`}
      />
      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        {/* 406 (#1369): at the top as well, so the customer knows before filling anything in. */}
        {paymentsPaused && (
          <PaymentPauseNotice
            testId="payment-pause-banner"
            className="rounded-xl bg-surface-card px-4 py-3 font-semibold"
          />
        )}
        {owesPenaltyNow && (
          <div
            data-testid="penalty-block"
            className="rounded-xl bg-red-900/40 px-4 py-3 text-sm text-red-100"
          >
            <p className="font-semibold">{PENALTY_UNPAID_SENTENCE}</p>
            <a
              href="/account/penalties"
              className="mt-3 inline-block rounded-full bg-primary px-4 py-2 font-bold text-black"
            >
              See my penalties
            </a>
          </div>
        )}
        {isBlocked && (
          <p className="text-sm text-red-400">
            This car is currently unavailable for booking — its operating hours do not allow any
            pickup or return time.
          </p>
        )}
        <TripContextStrip
          locationName={car?.location?.name || car?.location?.city_name}
          pickup={pickupAt}
          return={returnAt}
          onChange={({ pickup, return: ret }) => {
            // 304: pickup is locked to the parent's return_at for an extension — adjacency
            // (FR-006) is a hard server-side requirement, so the picker's own pickup control is
            // never allowed to move it, only the return date is the customer's to choose.
            if (!isExtending) setPickupAt(pickup);
            setReturnAt(ret);
          }}
          minDate={now}
          allowedWindow={allowedWindow}
          occupiedWindows={occupiedWindows}
          disabled={isBlocked}
          startExpanded
        />
        {/* While a new quote is in flight the previous one is still in state — the effect clears
            `quoteError` but deliberately not `quote`, because blanking the whole price summary on
            every keystroke would be worse. So suppress the verdict rather than clear the quote: a
            message must never describe a code the renter has already edited (FR-006). */}
        {/* 075: the verdict shown is the one paired to the code currently in the box. This
            subsumes the `quoting` guard the comment above describes — a request in flight has not
            produced a pair for the new code yet, so there is nothing to show — and it also holds
            the refusal on screen when the code stops being *sent* because a gift certificate is in
            play (FR-015), which a live-response read could not do. */}
        <VoucherStep code={voucherCode} onChange={setVoucherCode} verdict={currentVerdict} />

        <CreditStep
          credit={credit}
          applied={isCreditApplied}
          onChange={setUseCredit}
          isVoucherApplied={isVoucherApplied}
          isCodeEntered={giftCertCode.trim().length > 0}
          quote={
            isCreditApplied && quote?.credit
              ? {
                  applied: quote.credit.applied,
                  reason: quote.credit.applied ? undefined : quote.credit.reason,
                  credit_minor: quote.credit.applied ? quote.credit.credit_minor : undefined,
                  amount_due_minor: quote.amount_due_minor,
                }
              : undefined
          }
        />

        <GiftCertStep
          code={giftCertCode}
          onChange={setGiftCertCode}
          isCreditApplied={isCreditApplied}
          isVoucherApplied={isVoucherApplied}
          isVerdictPending={isVerdictPending}
          quote={
            quote?.gift_certificate
              ? {
                  applied: quote.gift_certificate.applied,
                  reason: quote.gift_certificate.reason,
                  credit_minor: quote.gift_certificate.credit_minor,
                  balance_after_minor: quote.gift_certificate.balance_after_minor,
                  amount_due_minor: quote.amount_due_minor,
                }
              : undefined
          }
        />

        {/* 416 (#1431, mock-up screen 10): the driving area before paying, so a per-km charge is never a surprise. Not
            on an extension: the customer already has the car and sees the area on their booking. */}
        {!isExtending && carRead.status === 'ok' && carRead.data?.pickup_zone && (
          <DrivingAreaCard zone={carRead.data.pickup_zone} when="booking" />
        )}

        {/* 417 (#1464, mock-up frame 3): the mileage package, where the quote offers it. */}
        {mileageOffer && (
          <MileagePackageStep
            offer={mileageOffer}
            value={mileageChoice}
            onChange={setMileageChoice}
          />
        )}

        {/* 361 (#980): above the price, as designed. Not on an extension, which keeps the original's answers. */}
        {!isExtending && <TripStep value={tripDraft} onChange={setTripDraft} />}

        {datesValid && (
          <div className="bg-surface-card rounded-xl p-4 flex flex-col gap-2">
            {quoting && (
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Spinner size="sm" /> Pricing…
              </div>
            )}
            {quoteError && <p className="text-sm text-red-400">{quoteError}</p>}
            {/* 091 US1/US2: the platform's refusal, in the renter's words. Rendered where the price
                would have been, because it is the answer to the same question. */}
            {refusalOutcome && !quoting && (
              <p data-testid="quote-refusal" className="text-sm text-red-400">
                {refusalSentence(refusalOutcome, quote?.minimum)}
              </p>
            )}
            {priced && quote && (
              <>
                <div className="flex justify-between text-sm text-gray-300">
                  <span>Subtotal</span>
                  <span>{rm(quote.total_minor ?? 0)}</span>
                </div>
                {/* 400 (#1354, mock-up screen 3): already in the subtotal; named so the customer sees why. */}
                {promoSavings(quote.breakdown).map((s) => (
                  <div
                    key={s.name}
                    data-testid="quote-promo-saving"
                    className="flex justify-between text-sm text-green-400"
                  >
                    <span>{`${s.name} · included`}</span>
                    <span>{`saved ${rm(s.saved_minor)}`}</span>
                  </div>
                ))}
                {quote.personal_discount?.applied && quote.personal_discount.discount_minor ? (
                  <div className="flex justify-between text-sm text-green-400">
                    <span>Personal discount</span>
                    <span>-{rm(quote.personal_discount.discount_minor)}</span>
                  </div>
                ) : null}
                {quote.voucher?.applied && quote.voucher.discount_minor ? (
                  <div className="flex justify-between text-sm text-green-400">
                    <span>Voucher discount</span>
                    <span>-{rm(quote.voucher.discount_minor)}</span>
                  </div>
                ) : null}
                {/* 417: the package after the discounts, which never reduce it. */}
                {quote.mileage_package && quote.mileage_package.price_minor > 0 ? (
                  <div
                    data-testid="summary-mileage"
                    className="flex justify-between text-sm text-gray-300"
                  >
                    <span>
                      {quote.mileage_package.allowance_km
                        ? `Mileage package (${quote.mileage_package.allowance_km} km)`
                        : 'Mileage package'}
                    </span>
                    <span>{rm(quote.mileage_package.price_minor)}</span>
                  </div>
                ) : null}
                {quote.platform_fee?.enabled && quote.platform_fee.amount_minor ? (
                  <div className="flex justify-between text-sm text-gray-300">
                    <span>Platform fee</span>
                    <span>{rm(quote.platform_fee.amount_minor)}</span>
                  </div>
                ) : null}
                {quote.channel_fee?.enabled && quote.channel_fee.amount_minor ? (
                  <div className="flex justify-between text-sm text-gray-300">
                    <span>Payment fee</span>
                    <span>{rm(quote.channel_fee.amount_minor)}</span>
                  </div>
                ) : null}
                {quote.tax && quote.tax.tax_minor > 0 && (
                  <div className="flex justify-between text-sm text-gray-300">
                    <span>
                      Tax (
                      {(quote.tax.rate_bps / 100).toFixed(quote.tax.rate_bps % 100 === 0 ? 0 : 2)}%)
                    </span>
                    <span>{rm(quote.tax.tax_minor)}</span>
                  </div>
                )}
                <div className="border-t border-surface-mid mt-1 pt-2 flex justify-between items-center">
                  <span className="text-sm text-gray-400">Total</span>
                  <span className="text-primary font-bold text-lg">
                    {rm(quote.grand_total_minor ?? quote.total_minor ?? 0)}
                  </span>
                </div>
                {/* 358 (FR-005): a credit code and "Use my credit" both read as Credit to the customer. */}
                {creditUsedMinor(quote) > 0 ? (
                  <>
                    <div
                      data-testid="summary-credit"
                      className="flex justify-between text-sm text-green-400"
                    >
                      <span>Credit</span>
                      <span>-{rm(creditUsedMinor(quote))}</span>
                    </div>
                    <div data-testid="summary-due" className="flex justify-between items-center">
                      <span className="text-sm text-gray-400">Amount due</span>
                      <span className="text-primary font-bold text-lg">
                        {rm(quote.amount_due_minor ?? 0)}
                      </span>
                    </div>
                  </>
                ) : null}
              </>
            )}
          </div>
        )}

        {/* 153 (#23): the agreement. A real <label> wrapping a real checkbox, so the whole
            sentence is the hit target on a phone and assistive technology reads the two as one
            control. The link is a plain anchor rather than a router push: it must work from a
            long-press "open in new tab" too, and going back must return the renter to this form
            with everything they have entered still in it (FR-004). */}
        <label className="flex items-start gap-3 text-sm text-gray-300">
          <input
            type="checkbox"
            checked={termsAgreed}
            onChange={(e) => setTermsAgreed((e.target as HTMLInputElement).checked)}
            className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
          />
          <span>
            I agree to the{' '}
            {/* 🔴 Opens in a NEW TAB deliberately (FR-004). Measured live on 2026-08-29: an
                in-app navigation to /terms unmounts this form, and coming back left the voucher
                box empty, the dates unset and this very checkbox unticked — the renter would have
                had to fill the whole form again for having read what they were agreeing to. */}
            <a
              href={LEGAL_DOCUMENT_PATHS.terms_of_service}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline"
            >
              Car Rental Terms &amp; Conditions
            </a>
            .
          </span>
        </label>

        {error && (
          <div>
            <p className="text-sm text-red-400">{error}</p>
            <AlternativeCars items={alternatives} />
          </div>
        )}
        {campusMismatch && (
          <div data-testid="booking-campus-mismatch" className="text-sm text-red-400">
            <p>This car isn't at your campus. You can only book cars at {user?.campus?.name}.</p>
            <a
              href={`/search?location_id=${user?.campus_location_id ?? ''}`}
              className="mt-1 inline-block text-primary underline"
            >
              See cars at {user?.campus?.name}
            </a>
          </div>
        )}
        {blockedReason && (
          <p data-testid="submit-blocked-reason" className="text-sm text-gray-400">
            {blockedReason}
          </p>
        )}
        {paymentsPaused ? (
          <PaymentPauseNotice className="" />
        ) : (
          <Button
            type="submit"
            loading={submitting}
            disabled={!canSubmit}
            size="lg"
            className="w-full"
          >
            Continue
          </Button>
        )}
      </form>
    </div>
  );
}
